export interface BlogSection {
  title: string;
  content: string[];
  icon?: string;
}

export interface BlogFAQ {
  question: string;
  answer: string;
}

export interface BlogArticle {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  excerpt: string;
  heroImage: string;
  thumbnailImage: string;
  contentImage?: string;
  author: string;
  authorImage: string;
  publishDate: string;
  readTime: string;
  category: string;
  tags: string[];
  sections: BlogSection[];
  faqs?: BlogFAQ[];
  seo: {
    title: string;
    description: string;
    keywords: string[];
    canonicalUrl: string;
    ogImage: string;
  };
}

export const blogArticles: BlogArticle[] = [
  {
    id: "1",
    slug: "remodelage-costal-ribxcare",
    title: "Remodelage Costal RibXcare",
    subtitle: "La technique révolutionnaire pour affiner votre taille",
    excerpt:
      "Découvrez le RibXcare, une technique chirurgicale innovante de remodelage des côtes permettant d'affiner la taille en modifiant la forme des côtes inférieures, sans ablation osseuse.",
    heroImage: "/assets/images/rib-care.jpeg",
    thumbnailImage: "/assets/images/rib-care.jpeg",
    contentImage: "/assets/images/cabinet.jpg",
    author: "Dr. Youssef Gam",
    authorImage: "/assets/images/youssef-avatar-2.jpeg",
    publishDate: "2026-02-01",
    readTime: "8 min",
    category: "Chirurgie du Corps",
    tags: [
      "RibXcare",
      "remodelage costal",
      "silhouette",
      "taille affinée",
      "chirurgie esthétique",
    ],
    sections: [
      {
        title: "Qu'est-ce que le remodelage costal RibXcare ?",
        icon: "🦴",
        content: [
          "Le RibXcare est une technique chirurgicale de remodelage des côtes permettant d'affiner la taille en modifiant la forme des côtes inférieures.",
          "L'os n'est pas retiré mais repositionné avec précision, via des micro‑accès cutanés discrets.",
          "Cette approche innovante offre des résultats naturels tout en minimisant les cicatrices et le temps de récupération.",
        ],
      },
      {
        title: "Quelle est la différence avec les techniques classiques ?",
        icon: "🔍",
        content: [
          "**Technique Classique :** Souvent plus invasive, parfois avec extraction partielle de côtes, cicatrices visibles et récupération longue.",
          "**RibXcare :** Pas d'ablation, guidage échographique, accès minimal, récupération plus rapide et cicatrices quasi invisibles.",
          "Le RibXcare représente une évolution majeure dans le domaine du remodelage corporel, offrant une alternative plus sûre et moins traumatisante.",
        ],
      },
      {
        title: "Comment se déroule l'intervention ?",
        icon: "🧠",
        content: [
          "1. **Évaluation préopératoire** et cartographie des côtes.",
          "2. **Anesthésie générale** ou rachianesthésie.",
          "3. **Micro‑accès** autour des côtes inférieures.",
          "4. Utilisation d'un **instrument à ultrasons** pour fragiliser et remodeler l'os.",
          "5. **Contrôle en temps réel** avec échographie.",
          "6. Port d'un **corset de contention** plusieurs semaines pour stabiliser la forme.",
        ],
      },
      {
        title: "Quels résultats peut-on attendre ?",
        icon: "📊",
        content: [
          "• Réduction du tour de taille et silhouette plus harmonieuse.",
          '• Effet "sablier" naturel et transition plus marquée entre taille, hanches et dos.',
          "• Résultats stables après quelques mois, une fois la consolidation osseuse terminée.",
        ],
      },
      {
        title: "Pour qui cette chirurgie est-elle indiquée ?",
        icon: "🧬",
        content: [
          "• Personnes avec silhouette carrée malgré faible graisse corporelle.",
          "• Ceux qui veulent un effet sablier plus marqué qu'avec la liposuccion seule.",
          "• Parfois en complément d'autres interventions pour une harmonisation thoraco‑abdominale.",
          "**Contre-indications :** maladies pulmonaires sévères, ostéoporose avancée, troubles majeurs de cicatrisation.",
        ],
      },
      {
        title: "Quels sont les risques et complications possibles ?",
        icon: "⚠️",
        content: [
          "• Douleur postopératoire modérée.",
          "• Œdème et ecchymoses autour des points de ponction.",
          "• Risque rare d'asymétrie, mauvaise consolidation osseuse, infection ou brûlure cutanée.",
          "• Convalescence de plusieurs semaines avec port de corset.",
        ],
      },
    ],
    seo: {
      title:
        "RibXcare Remodelage Costal Tunisie - Dr Youssef Gam | Affiner la Taille",
      description:
        "Découvrez le RibXcare, technique innovante de remodelage des côtes par Dr Youssef Gam en Tunisie. Affinez votre taille sans ablation osseuse. Résultats naturels et cicatrices invisibles.",
      keywords: [
        "ribxcare tunisie",
        "remodelage costal",
        "affiner taille chirurgie",
        "silhouette sablier",
        "chirurgie côtes tunisie",
        "dr youssef gam ribxcare",
        "remodelage des côtes",
        "chirurgie esthétique taille",
      ],
      canonicalUrl:
        "https://www.dryoussefgam.com/blog/remodelage-costal-ribxcare",
      ogImage: "/assets/images/rib-care.jpeg",
    },
  },
  {
    id: "2",
    slug: "chirurgie-lipoedeme-wal-bodytite",
    title: "Chirurgie du Lipœdème : WAL et BodyTite",
    subtitle: "Solutions chirurgicales avancées pour traiter le lipœdème",
    excerpt:
      "Le lipœdème est une accumulation symétrique de graisse résistante aux régimes. Découvrez les techniques WAL et BodyTite pour un traitement efficace et durable.",
    heroImage: "/assets/images/lipedema6.jpeg",
    thumbnailImage: "/assets/images/lipedema6.jpeg",
    contentImage: "/assets/images/lipedema5.jpg",
    author: "Dr. Youssef Gam",
    authorImage: "/assets/images/youssef-avatar-2.jpeg",
    publishDate: "2026-02-01",
    readTime: "10 min",
    category: "Chirurgie du Corps",
    tags: [
      "lipœdème",
      "WAL",
      "BodyTite",
      "liposuccion",
      "chirurgie esthétique",
    ],
    sections: [
      {
        title:
          "Qu'est-ce que le lipœdème et pourquoi envisager une chirurgie ?",
        icon: "❓",
        content: [
          "Le lipœdème est une accumulation symétrique de graisse résistante aux régimes et à l'exercice, touchant surtout les jambes et parfois les bras.",
          "Cette condition est souvent accompagnée de douleur, lourdeur et ecchymoses.",
          "La chirurgie est indiquée quand les méthodes conservatrices ne suffisent pas à améliorer la qualité de vie.",
        ],
      },
      {
        title: "Quel est l'objectif de la chirurgie ?",
        icon: "🩺",
        content: [
          "• Retirer la graisse pathologique de manière ciblée.",
          "• Réduire la douleur, la lourdeur et les gonflements.",
          "• Améliorer la silhouette et la mobilité au quotidien.",
          "• Optimiser la qualité de vie globale des patients.",
        ],
      },
      {
        title: "Quelles techniques sont utilisées ?",
        icon: "🛠️",
        content: [
          "**WAL (Water‑Assisted Liposuction) :**",
          "• Utilise un jet d'eau pour décoller et aspirer les cellules graisseuses tout en préservant les tissus lymphatiques.",
          "• Réduit le traumatisme et les ecchymoses par rapport à une liposuccion classique.",
          "",
          "**BodyTite :**",
          "• Technologie à radiofréquence qui améliore la rétraction cutanée après l'aspiration de graisse.",
          "• Souvent combinée avec WAL pour un résultat plus ferme et harmonieux.",
        ],
      },
      {
        title: "Déroulement de l'intervention",
        icon: "📋",
        content: [
          "1. **Consultation spécialisée** et planification des zones à traiter.",
          "2. **Anesthésie** générale ou locale selon l'étendue.",
          "3. **Micro‑incisions** pour l'insertion des canules WAL.",
          "4. **Retrait de la graisse** suivi de BodyTite pour la peau.",
          "5. Port d'un **vêtement compressif** et drainage lymphatique postopératoire.",
        ],
      },
      {
        title: "Récupération",
        icon: "⏱️",
        content: [
          "• Mobilisation légère dès le début de la récupération.",
          "• Compression continue pendant plusieurs semaines.",
          "• Œdèmes et ecchymoses fréquents les premiers jours.",
          "• Résultats visibles après quelques semaines, stabilisation en plusieurs mois.",
        ],
      },
      {
        title: "Risques et limites",
        icon: "⚠️",
        content: [
          "• Douleur, gonflement et ecchymoses transitoires.",
          "• Infection, irrégularités cutanées, troubles de la sensibilité possibles.",
          "• Risques liés à l'anesthésie.",
          "• Importance de préserver les tissus lymphatiques pour éviter le lymphœdème secondaire.",
        ],
      },
      {
        title: "Nombre de zones et séances",
        icon: "📌",
        content: [
          "• Plusieurs zones peuvent être traitées lors d'une même intervention.",
          "• Parfois plusieurs séances sont nécessaires selon l'étendue pour assurer sécurité et efficacité.",
        ],
      },
      {
        title: "Résultats attendus",
        icon: "📍",
        content: [
          "• Réduction significative du volume des zones touchées.",
          "• Amélioration de la douleur, de la lourdeur et de la mobilité.",
          "• Silhouette plus harmonieuse et meilleure qualité de vie.",
          "• Les résultats sont durables mais le suivi reste important car le lipœdème peut évoluer.",
        ],
      },
    ],
    faqs: [
      {
        question: "La chirurgie guérit-elle le lipœdème ?",
        answer:
          "Non, elle traite les symptômes et retire la graisse pathologique, mais la maladie reste chronique. Un suivi régulier est recommandé.",
      },
      {
        question: "Est-elle adaptée à toutes les patientes ?",
        answer:
          "L'éligibilité dépend du stade de la maladie et de l'état de santé général. Une évaluation personnalisée est indispensable avant toute intervention.",
      },
      {
        question: "Peut-on combiner WAL avec d'autres techniques ?",
        answer:
          "Oui, WAL est souvent associée à BodyTite pour améliorer la rétraction cutanée après liposuccion, offrant des résultats plus fermes et harmonieux.",
      },
    ],
    seo: {
      title:
        "Chirurgie Lipœdème Tunisie WAL BodyTite - Dr Youssef Gam | Traitement Efficace",
      description:
        "Traitement chirurgical du lipœdème en Tunisie par Dr Youssef Gam. Techniques WAL et BodyTite pour réduire la graisse pathologique, soulager la douleur et améliorer la qualité de vie.",
      keywords: [
        "lipœdème chirurgie tunisie",
        "wal liposuccion",
        "bodytite tunisie",
        "traitement lipœdème",
        "graisse résistante",
        "dr youssef gam lipœdème",
        "liposuccion assistée eau",
        "radiofréquence corps",
      ],
      canonicalUrl:
        "https://www.dryoussefgam.com/blog/chirurgie-lipoedeme-wal-bodytite",
      ogImage: "/assets/images/lipedema.webp",
    },
  },
  {
    id: "3",
    slug: "injections-botox-toxine-botulique",
    title: "Injections de Botox : Tout Savoir",
    subtitle: "Le traitement anti-rides le plus populaire au monde",
    excerpt:
      "Les injections de toxine botulique permettent de lisser les rides d'expression du visage de manière naturelle et sans chirurgie. Découvrez le déroulement, les résultats et les précautions.",
    heroImage: "/assets/images/botox1.jpg",
    thumbnailImage: "/assets/images/botox1.jpg",
    contentImage: "/assets/images/botox2.png",
    author: "Dr. Youssef Gam",
    authorImage: "/assets/images/youssef-avatar-2.jpeg",
    publishDate: "2026-03-05",
    readTime: "6 min",
    category: "Médecine Esthétique",
    tags: [
      "botox",
      "toxine botulique",
      "anti-rides",
      "injections",
      "médecine esthétique",
      "rajeunissement",
    ],
    sections: [
      {
        title: "Qu'est-ce que le Botox ?",
        icon: "💉",
        content: [
          "Le Botox, ou toxine botulique de type A, est une protéine purifiée utilisée en médecine esthétique pour détendre temporairement les muscles responsables des rides d'expression.",
          "Approuvé depuis plus de 20 ans, c'est le traitement esthétique non chirurgical le plus pratiqué dans le monde.",
          "Il agit en bloquant la transmission nerveuse au niveau des muscles ciblés, ce qui lisse la peau en surface sans altérer l'expressivité naturelle du visage lorsqu'il est bien dosé.",
        ],
      },
      {
        title: "Quelles zones peut-on traiter ?",
        icon: "🎯",
        content: [
          "Les zones les plus fréquemment traitées sont les rides du front (rides horizontales), la glabelle (rides du lion entre les sourcils) et les pattes d'oie (rides autour des yeux).",
          "Le Botox peut également être utilisé pour le sourire gingival, les plis d'amertume, les cordes du cou (platysma) et même la transpiration excessive (hyperhidrose).",
          "Chaque zone nécessite un dosage précis et une technique d'injection adaptée pour un résultat harmonieux.",
        ],
      },
      {
        title: "Comment se déroule une séance ?",
        icon: "🩺",
        content: [
          "La consultation commence par une analyse des expressions faciales au repos et en mouvement pour identifier les zones à traiter.",
          "Les injections sont réalisées avec des aiguilles très fines. La séance dure environ 15 à 20 minutes et ne nécessite aucune anesthésie dans la plupart des cas.",
          "Les résultats apparaissent progressivement entre 3 et 7 jours après l'injection et atteignent leur effet optimal à 2 semaines.",
          "L'effet dure en moyenne 4 à 6 mois, après quoi les injections peuvent être renouvelées.",
        ],
      },
      {
        title: "Résultats et suivi",
        icon: "✨",
        content: [
          "Un bon traitement au Botox doit donner un résultat naturel : la peau est lissée, le regard est plus reposé, mais les expressions restent vivantes.",
          "Un rendez-vous de contrôle à 15 jours permet d'ajuster le résultat si nécessaire avec une retouche ciblée.",
          "Avec des séances régulières, les rides tendent à s'atténuer durablement car les muscles perdent progressivement leur tonus excessif.",
        ],
      },
      {
        title: "Précautions et contre-indications",
        icon: "⚠️",
        content: [
          "Le Botox est contre-indiqué chez les femmes enceintes ou allaitantes, en cas de maladie neuromusculaire, ou d'allergie à l'un des composants.",
          "Il est recommandé d'éviter l'aspirine et les anti-inflammatoires quelques jours avant la séance pour réduire le risque d'ecchymoses.",
          "Les effets secondaires sont rares et transitoires : léger gonflement au point d'injection, légère asymétrie corrigeable, ou ecchymose minime.",
        ],
      },
    ],
    faqs: [
      {
        question: "Le Botox est-il douloureux ?",
        answer:
          "Les injections sont quasi indolores grâce à l'utilisation d'aiguilles très fines. Une sensation de picotement léger peut être ressentie mais ne nécessite généralement pas d'anesthésie.",
      },
      {
        question: "À partir de quel âge peut-on commencer le Botox ?",
        answer:
          "Il n'y a pas d'âge fixe. Le Botox préventif peut être envisagé dès l'apparition des premières rides d'expression, souvent autour de 25-30 ans, pour retarder leur installation.",
      },
      {
        question: "Le résultat est-il immédiat ?",
        answer:
          "Non, l'effet commence à apparaître entre 3 et 7 jours et atteint son maximum vers 2 semaines. C'est un processus progressif et naturel.",
      },
    ],
    seo: {
      title:
        "Injections Botox Tunisie - Dr Youssef Gam | Traitement Anti-Rides",
      description:
        "Injections de Botox en Tunisie par Dr Youssef Gam. Traitement anti-rides naturel du front, glabelle et pattes d'oie. Résultats visibles et durables.",
      keywords: [
        "botox tunisie",
        "injections toxine botulique",
        "anti-rides tunisie",
        "botox front",
        "pattes d'oie",
        "dr youssef gam botox",
        "médecine esthétique tunisie",
      ],
      canonicalUrl:
        "https://www.dryoussefgam.com/blog/injections-botox-toxine-botulique",
      ogImage: "/assets/images/botox1.jpg",
    },
  },
  {
    id: "4",
    slug: "lifting-fils-tenseurs-visage",
    title: "Lifting par Fils Tenseurs",
    subtitle: "Rajeunir et lifter le visage sans chirurgie",
    excerpt:
      "Les fils tenseurs offrent un effet lifting immédiat sans chirurgie. Découvrez cette technique innovante qui retend la peau et stimule la production de collagène pour un rajeunissement naturel.",
    heroImage: "/assets/images/threadLiftp.jpg",
    thumbnailImage: "/assets/images/threadLiftp.jpg",
    contentImage: "/assets/images/fils.webp",
    author: "Dr. Youssef Gam",
    authorImage: "/assets/images/youssef-avatar-2.jpeg",
    publishDate: "2026-03-10",
    readTime: "7 min",
    category: "Médecine Esthétique",
    tags: [
      "fils tenseurs",
      "lifting",
      "rajeunissement",
      "collagène",
      "médecine esthétique",
      "visage",
    ],
    sections: [
      {
        title: "Qu'est-ce que le lifting par fils tenseurs ?",
        icon: "🧵",
        content: [
          "Les fils tenseurs sont des fils résorbables en PDO ou PLLA insérés sous la peau pour lifter les tissus affaissés et stimuler la production naturelle de collagène.",
          "Cette technique permet de redéfinir l'ovale du visage, lifter les joues, les sourcils ou le cou sans aucune incision chirurgicale.",
          "Les fils se résorbent naturellement en 6 à 12 mois, mais l'effet tenseur et la stimulation du collagène se prolongent bien au-delà, jusqu'à 18 mois.",
        ],
      },
      {
        title: "Pour qui cette technique est-elle adaptée ?",
        icon: "👤",
        content: [
          "Le lifting par fils tenseurs est idéal pour les personnes présentant un relâchement cutané léger à modéré du visage et du cou.",
          "Il convient parfaitement aux patients souhaitant un rajeunissement visible sans les contraintes et le temps de récupération d'un lifting chirurgical.",
          "Souvent proposé entre 35 et 55 ans, il peut aussi être combiné avec d'autres traitements comme le Botox ou les fillers pour un résultat complet.",
        ],
      },
      {
        title: "Déroulement de la procédure",
        icon: "📋",
        content: [
          "La séance débute par un repérage des zones à traiter et une anesthésie locale pour un confort optimal.",
          "Les fils sont introduits à l'aide de fines aiguilles ou canules sous la peau, puis positionnés et ancrés pour exercer la traction souhaitée.",
          "La procédure dure entre 30 et 60 minutes selon le nombre de fils et les zones traitées. Le patient peut rentrer chez lui immédiatement après.",
        ],
      },
      {
        title: "Résultats et récupération",
        icon: "✨",
        content: [
          "L'effet lifting est visible immédiatement après la pose des fils, avec une amélioration progressive au fil des semaines grâce à la néocollagénèse.",
          "Un léger gonflement et quelques ecchymoses sont possibles les premiers jours. La reprise des activités normales se fait sous 2 à 3 jours.",
          "Il est recommandé d'éviter les mouvements buccaux excessifs, les soins du visage et le sport intense pendant 2 à 3 semaines.",
        ],
      },
      {
        title: "Risques et précautions",
        icon: "⚠️",
        content: [
          "Les complications sont rares lorsque la procédure est réalisée par un praticien expérimenté. Elles incluent : asymétrie, migration d'un fil, infection ou irritation locale.",
          "Le choix du type de fil et du protocole d'insertion est essentiel pour un résultat harmonieux et sans complication.",
          "Un suivi post-procédure à 15 jours puis à 3 mois permet de s'assurer de la bonne évolution du résultat.",
        ],
      },
    ],
    faqs: [
      {
        question: "Combien de fils sont nécessaires ?",
        answer:
          "Le nombre de fils varie selon les zones traitées et le degré de relâchement. En moyenne, 4 à 8 fils par côté du visage sont utilisés pour un lifting complet.",
      },
      {
        question: "Est-ce que ça fait mal ?",
        answer:
          "La procédure est réalisée sous anesthésie locale, ce qui la rend confortable. Une légère tension ou gêne peut être ressentie pendant quelques jours après la pose.",
      },
      {
        question:
          "Peut-on combiner les fils tenseurs avec d'autres traitements ?",
        answer:
          "Absolument. Les fils tenseurs se combinent très bien avec le Botox, l'acide hyaluronique ou le Morpheus8 pour un résultat rajeunissant global et harmonieux.",
      },
    ],
    seo: {
      title:
        "Lifting Fils Tenseurs Tunisie - Dr Youssef Gam | Rajeunissement Sans Chirurgie",
      description:
        "Lifting par fils tenseurs en Tunisie par Dr Youssef Gam. Rajeunissement du visage sans chirurgie, résultats naturels immédiats. Ovale du visage redéfini.",
      keywords: [
        "fils tenseurs tunisie",
        "lifting sans chirurgie",
        "rajeunissement visage",
        "fils pdo tunisie",
        "ovale visage",
        "dr youssef gam fils tenseurs",
        "lifting medical tunisie",
      ],
      canonicalUrl:
        "https://www.dryoussefgam.com/blog/lifting-fils-tenseurs-visage",
      ogImage: "/assets/images/threadLiftp.jpg",
    },
  },
  {
    id: "5",
    slug: "morpheus8-radiofrequence-fractionnee",
    title: "Morpheus8 : Radiofréquence Fractionnée",
    subtitle:
      "Le traitement révolutionnaire pour raffermir et rajeunir la peau",
    excerpt:
      "Le Morpheus8 combine micro-aiguilles et radiofréquence pour remodeler la peau en profondeur. Un traitement efficace contre le relâchement cutané, les rides et les cicatrices.",
    heroImage: "/assets/images/couv-actu-morpheus.webp",
    thumbnailImage: "/assets/images/couv-actu-morpheus.webp",
    contentImage: "/assets/images/morph2.jpg",
    author: "Dr. Youssef Gam",
    authorImage: "/assets/images/youssef-avatar-2.jpeg",
    publishDate: "2026-03-15",
    readTime: "7 min",
    category: "Médecine Esthétique",
    tags: [
      "Morpheus8",
      "radiofréquence",
      "micro-aiguilles",
      "raffermissement",
      "collagène",
      "peau",
    ],
    sections: [
      {
        title: "Qu'est-ce que le Morpheus8 ?",
        icon: "⚡",
        content: [
          "Le Morpheus8 est un dispositif médical de dernière génération qui associe le microneedling (micro-aiguilles) à la radiofréquence fractionnée pour traiter le relâchement cutané en profondeur.",
          "Les micro-aiguilles pénètrent la peau jusqu'à 4 mm de profondeur et délivrent une énergie de radiofréquence qui chauffe les tissus sous-cutanés, stimulant intensément la production de collagène et d'élastine.",
          "Ce traitement permet de remodeler et raffermir la peau du visage, du cou, du décolleté et du corps.",
        ],
      },
      {
        title: "Quelles sont les indications ?",
        icon: "🎯",
        content: [
          "Le Morpheus8 est indiqué pour le relâchement cutané du visage et du cou, les rides fines et profondes, les cicatrices d'acné, les vergetures et les pores dilatés.",
          "Il est particulièrement efficace pour redéfinir l'ovale du visage, traiter le double menton et améliorer la texture de la peau sur le corps.",
          "Ce traitement convient à tous les phototypes de peau, ce qui en fait une option universelle contrairement à certains lasers.",
        ],
      },
      {
        title: "Déroulement d'une séance",
        icon: "📋",
        content: [
          "Une crème anesthésiante est appliquée 30 à 45 minutes avant le traitement pour assurer un confort optimal.",
          "La pièce à main du Morpheus8 est appliquée sur les zones à traiter. Les micro-aiguilles pénètrent la peau et délivrent la radiofréquence de manière contrôlée.",
          "La séance dure entre 30 et 60 minutes selon l'étendue des zones traitées. Un protocole de 2 à 3 séances espacées de 4 à 6 semaines est généralement recommandé.",
        ],
      },
      {
        title: "Résultats et récupération",
        icon: "✨",
        content: [
          "Les premiers résultats sont visibles dès 2 à 3 semaines, avec une amélioration progressive sur 3 à 6 mois à mesure que le collagène se reconstruit.",
          "La peau est légèrement rouge et sensible pendant 2 à 3 jours. De petites croûtes microscopiques peuvent apparaître et disparaissent en quelques jours.",
          "La reprise des activités sociales est possible dès le lendemain avec un maquillage léger. Le sport intense est à éviter pendant une semaine.",
        ],
      },
      {
        title: "Avantages par rapport aux autres traitements",
        icon: "🏆",
        content: [
          "Le Morpheus8 offre des résultats comparables à un mini-lifting sans chirurgie ni temps d'arrêt prolongé.",
          "Contrairement aux lasers ablatifs, il convient à toutes les carnations de peau sans risque d'hyperpigmentation.",
          "La profondeur de pénétration est ajustable, permettant un traitement personnalisé selon les besoins de chaque patient.",
        ],
      },
    ],
    faqs: [
      {
        question: "Combien de séances sont nécessaires ?",
        answer:
          "En général, 2 à 3 séances espacées de 4 à 6 semaines sont recommandées pour un résultat optimal. Une séance d'entretien annuelle permet de maintenir les résultats.",
      },
      {
        question: "Le Morpheus8 est-il douloureux ?",
        answer:
          "Grâce à l'application d'une crème anesthésiante avant la séance, le traitement est bien toléré. Une sensation de chaleur est ressentie pendant la procédure.",
      },
      {
        question: "Peut-on combiner le Morpheus8 avec d'autres traitements ?",
        answer:
          "Oui, le Morpheus8 se combine parfaitement avec le PRP (plasma riche en plaquettes), l'acide hyaluronique ou le Botox pour un résultat rajeunissant complet.",
      },
    ],
    seo: {
      title: "Morpheus8 Tunisie - Dr Youssef Gam | Radiofréquence Fractionnée",
      description:
        "Traitement Morpheus8 en Tunisie par Dr Youssef Gam. Radiofréquence fractionnée pour raffermir la peau, traiter les rides et cicatrices. Résultats naturels sans chirurgie.",
      keywords: [
        "morpheus8 tunisie",
        "radiofréquence fractionnée",
        "microneedling tunisie",
        "raffermissement peau",
        "rides traitement",
        "dr youssef gam morpheus8",
        "collagène stimulation",
      ],
      canonicalUrl:
        "https://www.dryoussefgam.com/blog/morpheus8-radiofrequence-fractionnee",
      ogImage: "/assets/images/couv-actu-morpheus.webp",
    },
  },
  {
    id: "6",
    slug: "acide-hyaluronique-injections-comblement",
    title: "Acide Hyaluronique : Injections de Comblement",
    subtitle: "Restaurer les volumes et sublimer les traits du visage",
    excerpt:
      "L'acide hyaluronique est le produit de comblement le plus utilisé en médecine esthétique. Il permet de restaurer les volumes du visage, repulper les lèvres et combler les rides.",
    heroImage: "/assets/images/filler.jpeg",
    thumbnailImage: "/assets/images/filler.jpeg",
    contentImage: "/assets/images/filler2bg.png",
    author: "Dr. Youssef Gam",
    authorImage: "/assets/images/youssef-avatar-2.jpeg",
    publishDate: "2026-03-18",
    readTime: "8 min",
    category: "Médecine Esthétique",
    tags: [
      "acide hyaluronique",
      "filler",
      "comblement",
      "lèvres",
      "volumes",
      "médecine esthétique",
    ],
    sections: [
      {
        title: "Qu'est-ce que l'acide hyaluronique ?",
        icon: "💧",
        content: [
          "L'acide hyaluronique est une molécule naturellement présente dans notre peau, responsable de son hydratation, son volume et son élasticité.",
          "Avec l'âge, sa production diminue, entraînant l'apparition de rides, une perte de volume et un relâchement cutané.",
          "Les injections d'acide hyaluronique permettent de compenser cette perte en restaurent les volumes et en hydratant la peau de l'intérieur.",
        ],
      },
      {
        title: "Quelles zones peut-on traiter ?",
        icon: "🎯",
        content: [
          "Le visage est la zone principale : lèvres (augmentation et redéfinition), pommettes, cernes, sillons nasogéniens, vallée des larmes, menton et mâchoire.",
          "Les mains peuvent également être traitées pour masquer les veines apparentes et redonner du volume.",
          "Chaque zone nécessite un produit de densité et de réticulation spécifique pour un résultat naturel et adapté.",
        ],
      },
      {
        title: "Déroulement des injections",
        icon: "💉",
        content: [
          "La séance commence par une analyse des proportions du visage et une discussion des objectifs avec le patient.",
          "Une crème anesthésiante ou un bloc nerveux peut être utilisé pour les zones sensibles comme les lèvres.",
          "Les injections sont réalisées avec des aiguilles fines ou des canules souples selon la zone. La séance dure 20 à 40 minutes.",
          "Le résultat est immédiatement visible, avec un aspect définitif après 10 à 15 jours une fois l'œdème résorbé.",
        ],
      },
      {
        title: "Durée des résultats",
        icon: "⏱️",
        content: [
          "Les résultats durent de 6 à 18 mois selon le produit utilisé, la zone traitée et le métabolisme du patient.",
          "Les lèvres métabolisent le produit plus rapidement (6 à 9 mois), tandis que les pommettes et la mâchoire peuvent conserver le résultat jusqu'à 18 mois.",
          "Des séances d'entretien permettent de maintenir le résultat dans le temps de manière progressive et naturelle.",
        ],
      },
      {
        title: "Risques et effets secondaires",
        icon: "⚠️",
        content: [
          "Les effets secondaires courants sont bénins et transitoires : rougeur, gonflement, ecchymoses au point d'injection pendant 2 à 5 jours.",
          "Plus rarement, une asymétrie mineure peut survenir et être corrigée lors d'un rendez-vous de retouche.",
          "L'acide hyaluronique présente l'avantage d'être réversible : en cas de résultat insatisfaisant, une enzyme (hyaluronidase) peut dissoudre le produit.",
        ],
      },
    ],
    faqs: [
      {
        question: "L'injection est-elle douloureuse ?",
        answer:
          "La plupart des produits contiennent un anesthésiant intégré (lidocaïne). Une crème anesthésiante est également appliquée avant la séance. La procédure est très bien tolérée.",
      },
      {
        question: "Peut-on reprendre ses activités immédiatement ?",
        answer:
          "Oui, les injections d'acide hyaluronique ne nécessitent aucune éviction sociale. Un léger maquillage peut être appliqué dès le lendemain.",
      },
      {
        question: "Quelle est la différence avec le Botox ?",
        answer:
          "Le Botox détend les muscles pour lisser les rides d'expression, tandis que l'acide hyaluronique comble les volumes perdus et les rides statiques. Les deux sont complémentaires.",
      },
    ],
    seo: {
      title:
        "Acide Hyaluronique Tunisie - Dr Youssef Gam | Injections Comblement",
      description:
        "Injections d'acide hyaluronique en Tunisie par Dr Youssef Gam. Comblement des rides, augmentation des lèvres, restauration des volumes. Résultats naturels immédiats.",
      keywords: [
        "acide hyaluronique tunisie",
        "filler tunisie",
        "comblement rides",
        "augmentation lèvres",
        "volumes visage",
        "dr youssef gam filler",
        "injections comblement tunisie",
      ],
      canonicalUrl:
        "https://www.dryoussefgam.com/blog/acide-hyaluronique-injections-comblement",
      ogImage: "/assets/images/filler.jpeg",
    },
  },
  {
    id: "7",
    slug: "sculptra-bio-stimulation-collagene",
    title: "Sculptra : Bio-Stimulation du Collagène",
    subtitle: "Rajeunir en stimulant naturellement la production de collagène",
    excerpt:
      "Le Sculptra est un bio-stimulateur injectable qui relance la production de collagène pour un rajeunissement progressif et durable du visage et du corps.",
    heroImage: "/assets/images/sculptrabg.jpg",
    thumbnailImage: "/assets/images/sculptrabg.jpg",
    contentImage: "/assets/images/beautiful-girl-with-wet-skin.jpg",
    author: "Dr. Youssef Gam",
    authorImage: "/assets/images/youssef-avatar-2.jpeg",
    publishDate: "2026-03-20",
    readTime: "6 min",
    category: "Médecine Esthétique",
    tags: [
      "Sculptra",
      "collagène",
      "bio-stimulation",
      "rajeunissement",
      "PLLA",
      "visage",
    ],
    sections: [
      {
        title: "Qu'est-ce que le Sculptra ?",
        icon: "🔬",
        content: [
          "Le Sculptra est un injectable à base d'acide poly-L-lactique (PLLA), un polymère biodégradable et biocompatible utilisé en médecine depuis des décennies.",
          "Contrairement aux fillers classiques qui comblent mécaniquement, le Sculptra agit comme un bio-stimulateur : il relance la production naturelle de collagène par les propres cellules du patient.",
          "Le résultat est un rajeunissement progressif et naturel, souvent décrit comme un effet « bonne mine » sans que l'entourage ne puisse identifier un traitement.",
        ],
      },
      {
        title: "Quelles zones peuvent être traitées ?",
        icon: "🎯",
        content: [
          "Le visage est la zone de prédilection : tempes creuses, joues affinées, sillons nasogéniens, mâchoire et menton.",
          "Le Sculptra est aussi utilisé sur le corps : fesses (Sculptra Butt Lift), mains, bras et décolleté pour redensifier la peau.",
          "Son action globale en fait un traitement de choix pour les patients présentant une perte de volume diffuse liée au vieillissement.",
        ],
      },
      {
        title: "Protocole de traitement",
        icon: "📋",
        content: [
          "Le protocole standard comprend 2 à 3 séances espacées de 4 à 6 semaines pour permettre au collagène de se reconstruire entre chaque session.",
          "Chaque séance dure environ 30 minutes. Le produit est injecté en profondeur avec une canule ou une aiguille selon les zones.",
          "Un massage régulier de la zone traitée pendant 5 jours est essentiel pour assurer une répartition homogène du produit.",
        ],
      },
      {
        title: "Résultats et durabilité",
        icon: "⏱️",
        content: [
          "Les résultats apparaissent progressivement sur 2 à 3 mois à mesure que le collagène se reconstruit autour des micro-particules.",
          "L'effet dure en moyenne 2 ans, ce qui est nettement plus long que la plupart des fillers à base d'acide hyaluronique.",
          "Le rajeunissement obtenu est très naturel car il repose sur la propre biologie du patient.",
        ],
      },
      {
        title: "Effets secondaires et précautions",
        icon: "⚠️",
        content: [
          "Les effets secondaires sont généralement légers : rougeur, gonflement et sensibilité au point d'injection pendant quelques jours.",
          "Le massage post-injection est essentiel pour éviter la formation de petits nodules sous-cutanés.",
          "Le Sculptra est contre-indiqué en cas de maladie auto-immune active, d'infection cutanée dans la zone à traiter, ou de grossesse.",
        ],
      },
    ],
    faqs: [
      {
        question:
          "Quelle est la différence entre le Sculptra et l'acide hyaluronique ?",
        answer:
          "L'acide hyaluronique comble immédiatement les rides et volumes. Le Sculptra stimule la production de collagène pour un résultat progressif mais plus durable (jusqu'à 2 ans).",
      },
      {
        question: "Le Sculptra est-il adapté aux jeunes patients ?",
        answer:
          "Le Sculptra est principalement indiqué à partir de 30-35 ans, lorsque la perte de collagène commence à se manifester. Il peut être utilisé de manière préventive.",
      },
      {
        question: "Combien coûte un traitement Sculptra ?",
        answer:
          "Le coût dépend du nombre de flacons utilisés et des zones traitées. Une consultation personnalisée permet d'établir un devis précis adapté à vos besoins.",
      },
    ],
    seo: {
      title: "Sculptra Tunisie - Dr Youssef Gam | Bio-Stimulation Collagène",
      description:
        "Traitement Sculptra en Tunisie par Dr Youssef Gam. Bio-stimulation du collagène pour un rajeunissement naturel et durable du visage et du corps.",
      keywords: [
        "sculptra tunisie",
        "bio-stimulation collagène",
        "acide poly-l-lactique",
        "rajeunissement naturel",
        "sculptra visage",
        "dr youssef gam sculptra",
        "collagène stimulation tunisie",
      ],
      canonicalUrl:
        "https://www.dryoussefgam.com/blog/sculptra-bio-stimulation-collagene",
      ogImage: "/assets/images/sculptrabg.jpg",
    },
  },
  {
    id: "8",
    slug: "dr-youssef-gam-certifie-ax-scientific-lab-tunisie",
    title:
      "Dr Youssef Gam : le seul chirurgien certifié AX Scientific Lab en Tunisie",
    subtitle:
      "Une reconnaissance internationale dans le domaine du remodelage costal",
    excerpt:
      "Le Dr Youssef Gam bénéficie de la certification AX Scientific Lab, le centre de formation fondé par le Dr Raúl Manzaneda Cipriani, créateur de la technique RibXcar. Sous réserve de vérification du registre officiel, il serait le seul chirurgien certifié AX Scientific Lab en Tunisie.",
    heroImage: "/assets/images/ax-scientific-lab-certification.jpeg",
    thumbnailImage: "/assets/images/ax-scientific-lab-certification.jpeg",
    contentImage: "/assets/images/rib-care.jpeg",
    author: "Dr. Youssef Gam",
    authorImage: "/assets/images/youssef-avatar-2.jpeg",
    publishDate: "2026-09-07",
    readTime: "6 min",
    category: "Chirurgie du Corps",
    tags: [
      "AX Scientific Lab",
      "RibXcar",
      "remodelage costal",
      "certification",
      "Raúl Manzaneda",
      "chirurgie esthétique",
    ],
    sections: [
      {
        title:
          "Une reconnaissance internationale dans le domaine du remodelage costal",
        icon: "🏆",
        content: [
          "Le Dr Youssef Gam, chirurgien plasticien et esthétique à Tunis, se distingue par son engagement dans les techniques modernes de body contouring et de remodelage de la silhouette.",
          "Formé à la technique RibXcar, développée par le Dr Raúl Manzaneda Cipriani, le Dr Youssef Gam bénéficie de la certification délivrée par AX Scientific Lab, le centre de formation fondé par le créateur de cette technique.",
          "À notre connaissance et sous réserve de vérification du registre officiel AX Scientific Lab à la date de publication, le Dr Youssef Gam est le seul chirurgien certifié AX Scientific Lab en Tunisie.",
        ],
      },
      {
        title: "AX Scientific Lab : l'académie du Dr Raúl Manzaneda",
        icon: "🎓",
        content: [
          "AX Scientific Lab est le centre scientifique et de formation créé par le Dr Raúl Manzaneda Cipriani, chirurgien plasticien péruvien et créateur de la technique RibXcar.",
          "Cette académie forme des chirurgiens plasticiens à travers le monde aux principes anatomiques et techniques du remodelage costal RibXcar.",
          "La formation comprend notamment une approche théorique et pratique de la technique, avec une formation spécialisée destinée aux chirurgiens plasticiens qualifiés.",
        ],
      },
      {
        title: "RibXcar : une nouvelle approche du remodelage costal",
        icon: "🦴",
        content: [
          "La technique RibXcar a été développée pour le remodelage costal à travers une approche utilisant notamment le guidage échographique.",
          "Elle fait l'objet de publications scientifiques dans des revues spécialisées, notamment PRS Global Open et Aesthetic Surgery Journal.",
          "Le Dr Youssef Gam s'inscrit ainsi dans une démarche de formation continue et d'ouverture aux innovations internationales dans le domaine du body contouring.",
        ],
      },
      {
        title: "Une expertise internationale au service des patients tunisiens",
        icon: "🌍",
        content: [
          "La chirurgie esthétique moderne évolue constamment.",
          "Nouvelles technologies, amélioration des connaissances anatomiques, guidage par imagerie et développement de nouvelles techniques nécessitent une formation continue et rigoureuse.",
          "Pour le Dr Youssef Gam, l'innovation ne doit jamais être dissociée de la sécurité, de la sélection des indications et de l'information du patient.",
          "Chaque intervention nécessite une évaluation individuelle et une décision médicale adaptée à la morphologie, aux antécédents et aux objectifs de chaque patient.",
        ],
      },
      {
        title: "La Tunisie connectée aux grandes innovations internationales",
        icon: "🔗",
        content: [
          "Grâce à son parcours et à ses formations internationales, le Dr Youssef Gam souhaite participer au développement d'une chirurgie esthétique tunisienne connectée aux évolutions scientifiques et technologiques mondiales.",
          "La certification AX Scientific Lab constitue une reconnaissance de sa formation officielle à la technique RibXcar auprès de l'académie fondée par son créateur.",
        ],
      },
    ],
    seo: {
      title:
        "Dr Youssef Gam Certifié AX Scientific Lab - Seul Chirurgien en Tunisie",
      description:
        "Le Dr Youssef Gam est certifié AX Scientific Lab, académie du Dr Raúl Manzaneda Cipriani créateur de la technique RibXcar. Découvrez cette reconnaissance internationale en Tunisie.",
      keywords: [
        "ax scientific lab tunisie",
        "dr youssef gam certification",
        "ribxcar tunisie",
        "raul manzaneda cipriani",
        "chirurgien certifié ax scientific lab",
        "remodelage costal tunisie",
        "body contouring tunisie",
      ],
      canonicalUrl:
        "https://www.dryoussefgam.com/blog/dr-youssef-gam-certifie-ax-scientific-lab-tunisie",
      ogImage: "/assets/images/ax-scientific-lab-certification.jpeg",
    },
  },
  {
    id: "9",
    slug: "dr-youssef-gam-lipoedeme-total-definer-remodelage-corporel",
    title:
      "Dr Youssef Gam : une expertise dédiée au lipœdème et au remodelage corporel avancé",
    subtitle:
      "Expertise chirurgicale, technologies avancées et prise en charge globale de la silhouette",
    excerpt:
      "Formé à l'approche Total Definer, le Dr Youssef Gam poursuit son développement dans le remodelage corporel haute définition, avec un intérêt particulier pour la prise en charge du lipœdème, une maladie chronique encore trop souvent méconnue.",
    heroImage: "/assets/images/total-definer.jpeg",
    thumbnailImage: "/assets/images/total-definer2.jpeg",
    contentImage: "/assets/images/total-definer2.jpeg",
    author: "Dr. Youssef Gam",
    authorImage: "/assets/images/youssef-avatar-2.jpeg",
    publishDate: "2026-10-26",
    readTime: "7 min",
    category: "Chirurgie du Corps",
    tags: [
      "lipœdème",
      "Total Definer",
      "remodelage corporel",
      "haute définition",
      "liposuccion",
      "chirurgie esthétique",
    ],
    sections: [
      {
        title: "Une expertise dédiée au lipœdème et au remodelage corporel",
        icon: "🩺",
        content: [
          "Dr Youssef Gam, chirurgien spécialisé en chirurgie plastique et esthétique, développe une approche du lipœdème associant expertise chirurgicale, technologies avancées et prise en charge globale de la silhouette.",
          "Formé à l'approche Total Definer, Dr Gam poursuit son développement professionnel dans le domaine du remodelage corporel haute définition, avec un intérêt particulier pour la prise en charge du lipœdème, une maladie chronique encore trop souvent méconnue.",
        ],
      },
      {
        title: "Le lipœdème : une prise en charge spécifique",
        icon: "🔬",
        content: [
          "Le lipœdème est une affection chronique caractérisée notamment par une accumulation anormale et disproportionnée de tissu adipeux, principalement au niveau des membres inférieurs, parfois des bras.",
          "Il peut s'accompagner de douleurs, d'une sensibilité au toucher, d'ecchymoses faciles et d'un retentissement important sur la qualité de vie.",
          "Sa prise en charge nécessite une évaluation individualisée, tenant compte du stade clinique, des symptômes, de la mobilité, des éventuelles pathologies associées et des attentes de chaque patiente.",
        ],
      },
      {
        title: "L'approche Total Definer appliquée au lipœdème",
        icon: "✨",
        content: [
          "L'approche Total Definer s'inscrit dans une philosophie de remodelage corporel précis, harmonieux et adapté à l'anatomie de chaque personne.",
          "Dans la prise en charge chirurgicale du lipœdème, la stratégie peut inclure, selon les indications, des techniques de liposuccion adaptées, notamment assistées par jet d'eau ou par ultrasons. Le choix de la technique repose sur l'évaluation clinique, la sécurité opératoire et les objectifs thérapeutiques.",
          "L'objectif n'est pas simplement de réduire un volume, mais de rechercher une amélioration fonctionnelle et morphologique, tout en respectant les tissus et en tenant compte des risques propres à cette pathologie.",
        ],
      },
      {
        title: "Une vision globale, au-delà de la chirurgie",
        icon: "🌿",
        content: [
          "La chirurgie ne constitue pas à elle seule une réponse universelle au lipœdème.",
          "Une prise en charge globale peut également intégrer l'activité physique adaptée, les mesures de compression, la gestion de la douleur et, lorsque cela est indiqué, un accompagnement spécialisé.",
          "Chaque parcours doit être personnalisé, avec des objectifs réalistes et un suivi postopératoire rigoureux.",
        ],
      },
      {
        title: "Dr Youssef Gam : formation, précision et engagement",
        icon: "🎓",
        content: [
          "À travers son développement dans l'univers Total Definer et son intérêt pour les techniques avancées de remodelage corporel, Dr Youssef Gam souhaite contribuer à une meilleure reconnaissance du lipœdème et à une prise en charge adaptée aux besoins de chaque patiente.",
          "Son engagement : associer précision chirurgicale, réflexion médicale et approche individualisée pour accompagner les patientes dans leur parcours de soins.",
          "Information médicale : les résultats et les bénéfices potentiels varient selon les patientes. Une consultation spécialisée est indispensable pour confirmer le diagnostic, discuter des indications et présenter les risques, les limites et les alternatives thérapeutiques.",
        ],
      },
    ],
    faqs: [
      {
        question: "Qu'est-ce que l'approche Total Definer ?",
        answer:
          "Il s'agit d'une philosophie de remodelage corporel haute définition, précise et adaptée à l'anatomie de chaque personne, à laquelle le Dr Youssef Gam s'est formé.",
      },
      {
        question: "La chirurgie suffit-elle à traiter le lipœdème ?",
        answer:
          "Non. La chirurgie n'est pas à elle seule une réponse universelle. Une prise en charge globale peut associer activité physique adaptée, compression, gestion de la douleur et accompagnement spécialisé.",
      },
      {
        question: "Quelles techniques peuvent être utilisées ?",
        answer:
          "Selon les indications, des techniques de liposuccion adaptées, notamment assistées par jet d'eau ou par ultrasons. Le choix repose sur l'évaluation clinique, la sécurité opératoire et les objectifs thérapeutiques.",
      },
      {
        question:
          "Les résultats sont-ils identiques pour toutes les patientes ?",
        answer:
          "Non, les résultats et bénéfices potentiels varient selon les patientes. Une consultation spécialisée est indispensable pour confirmer le diagnostic et présenter les risques, limites et alternatives.",
      },
    ],
    seo: {
      title:
        "Dr Youssef Gam Lipœdème & Total Definer - Remodelage Corporel Avancé",
      description:
        "Dr Youssef Gam, formé à l'approche Total Definer, développe une prise en charge du lipœdème associant expertise chirurgicale, technologies avancées et vision globale de la silhouette.",
      keywords: [
        "lipœdème tunisie",
        "total definer",
        "dr youssef gam lipoedeme",
        "remodelage corporel haute définition",
        "liposuccion jet d'eau",
        "liposuccion ultrasons",
        "chirurgie lipoedeme",
        "body contouring tunisie",
      ],
      canonicalUrl:
        "https://www.dryoussefgam.com/blog/dr-youssef-gam-lipoedeme-total-definer-remodelage-corporel",
      ogImage: "/assets/images/total-definer.jpeg",
    },
  },
];

export const getBlogArticleBySlug = (slug: string): BlogArticle | undefined => {
  return blogArticles.find((article) => article.slug === slug);
};

export const getRelatedArticles = (
  currentSlug: string,
  limit: number = 3,
): BlogArticle[] => {
  const currentArticle = getBlogArticleBySlug(currentSlug);
  if (!currentArticle) return blogArticles.slice(0, limit);

  return blogArticles
    .filter((article) => article.slug !== currentSlug)
    .filter(
      (article) =>
        article.category === currentArticle.category ||
        article.tags.some((tag) => currentArticle.tags.includes(tag)),
    )
    .slice(0, limit);
};

export const blogPageSEO = {
  title:
    "Blog Chirurgie Esthétique - Dr Youssef Gam | Actualités, Conseils et Techniques",
  description:
    "Découvrez les dernières actualités et conseils en chirurgie esthétique et plastique par Dr Youssef Gam à Tunis et Paris. Articles détaillés sur les techniques innovantes, conseils pré et post-opératoires, médecine esthétique.",
  keywords: [
    "blog chirurgie esthétique",
    "actualités chirurgie plastique",
    "conseils chirurgie tunisie",
    "conseils chirurgie paris",
    "dr youssef gam blog",
    "techniques chirurgicales innovantes",
    "médecine esthétique tunisie",
    "médecine esthétique paris",
    "articles chirurgie esthétique",
    "blog médecine esthétique",
  ],
  canonicalUrl: "https://www.dryoussefgam.com/blog",
  ogImage: "https://www.dryoussefgam.com/logo-black.png",
};
